import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {CopilotAppIcon} from '@github-ui/copilot-avatar/CopilotAppIcon'
import {sendEvent} from '@github-ui/hydro-analytics'
import {TerminalIcon} from '@primer/octicons-react'
import {ActionList, VisuallyHidden} from '@primer/react'

import editors, {xcodeInstallPageUrl} from './editors'

export const COPILOT_APP_DOWNLOAD_LABEL = 'GitHub Copilot app'
export const COPILOT_APP_DOWNLOAD_URL = 'https://github.com/features/ai/github-app'
export const COPILOT_CLI_DOCS_URL =
  'https://docs.github.com/en/copilot/managing-copilot/configure-personal-settings/installing-github-copilot-in-the-cli'
export const COPILOT_CLI_MARKETING_URL = 'https://github.com/features/copilot/cli'

const COPILOT_APP_DOWNLOAD_EVENT_NAME = 'github_copilot_app'
const COPILOT_CLI_DOWNLOAD_EVENT_NAME = 'cli'
const COPILOT_CLI_DOWNLOAD_LABEL = 'GitHub Copilot CLI'
const MENU_ITEM_CLICK_ACTION = 'menu_item_click'
const NEW_WINDOW_LINK_PROPS = {target: '_blank', rel: 'noopener noreferrer'} as const
const COPILOT_CLI_ICON_VIEWPORT_STYLE = {
  display: 'grid',
  height: 20,
  lineHeight: 0,
  placeItems: 'center',
  width: 20,
} as const

function renderNewWindowLabel(label: string) {
  return (
    <>
      {label}
      <VisuallyHidden> (opens in new tab)</VisuallyHidden>
    </>
  )
}

export interface EditorMenuItemsProps {
  menuLocation: string
  mode: string
  onItemClick?: () => void
}

interface DownloadMenuItemProps {
  menuLocation: string
  mode: string
  onFallbackAnalyticsClick?: () => void
}

interface CopilotCliDownloadMenuItemProps extends DownloadMenuItemProps {
  href: string
}

export interface CopilotDownloadMenuItemsProps {
  cliHref: string
  menuLocation: string
  mode: string
  onAppFallbackAnalyticsClick?: () => void
  onCliFallbackAnalyticsClick?: () => void
}

export interface MenuAnalyticsProps {
  eventName: string
  menuLocation: string
  mode?: string
  action?: string
  text?: string
}

export function recordMenuClick({eventName, menuLocation, mode, action, text}: MenuAnalyticsProps) {
  const target = `${menuLocation.toUpperCase()}_${eventName.toUpperCase()}`
  const metadata = {
    target,
    text,
    category: menuLocation,
    action,
    mode,
  }
  sendEvent('dotcom_chat.activate', metadata)
}

function recordDownloadMenuItemClick({
  eventName,
  menuLocation,
  mode,
  text,
  onFallbackAnalyticsClick,
}: DownloadMenuItemProps & {eventName: string; text: string}) {
  if (copilotFeatureFlags.freeToPaidTelemetry) {
    recordMenuClick({eventName, menuLocation, mode, action: MENU_ITEM_CLICK_ACTION, text})
  } else {
    onFallbackAnalyticsClick?.()
  }
}

export function CopilotAppDownloadMenuItem({menuLocation, mode, onFallbackAnalyticsClick}: DownloadMenuItemProps) {
  return (
    <ActionList.LinkItem
      href={COPILOT_APP_DOWNLOAD_URL}
      {...NEW_WINDOW_LINK_PROPS}
      onClick={() => {
        recordDownloadMenuItemClick({
          eventName: COPILOT_APP_DOWNLOAD_EVENT_NAME,
          menuLocation,
          mode,
          text: COPILOT_APP_DOWNLOAD_LABEL,
          onFallbackAnalyticsClick,
        })
      }}
    >
      {renderNewWindowLabel(COPILOT_APP_DOWNLOAD_LABEL)}
      <ActionList.LeadingVisual>
        <CopilotAppIcon />
      </ActionList.LeadingVisual>
    </ActionList.LinkItem>
  )
}

export function CopilotCliDownloadMenuItem({
  href,
  menuLocation,
  mode,
  onFallbackAnalyticsClick,
}: CopilotCliDownloadMenuItemProps) {
  return (
    <ActionList.LinkItem
      href={href}
      {...NEW_WINDOW_LINK_PROPS}
      onClick={() => {
        recordDownloadMenuItemClick({
          eventName: COPILOT_CLI_DOWNLOAD_EVENT_NAME,
          menuLocation,
          mode,
          text: COPILOT_CLI_DOWNLOAD_LABEL,
          onFallbackAnalyticsClick,
        })
      }}
    >
      {renderNewWindowLabel(COPILOT_CLI_DOWNLOAD_LABEL)}
      <ActionList.LeadingVisual>
        <span aria-hidden="true" style={COPILOT_CLI_ICON_VIEWPORT_STYLE}>
          <TerminalIcon size={16} />
        </span>
      </ActionList.LeadingVisual>
    </ActionList.LinkItem>
  )
}

export function CopilotDownloadMenuItems({
  cliHref,
  menuLocation,
  mode,
  onAppFallbackAnalyticsClick,
  onCliFallbackAnalyticsClick,
}: CopilotDownloadMenuItemsProps) {
  return (
    <>
      <CopilotAppDownloadMenuItem
        menuLocation={menuLocation}
        mode={mode}
        onFallbackAnalyticsClick={onAppFallbackAnalyticsClick}
      />
      <CopilotCliDownloadMenuItem
        href={cliHref}
        menuLocation={menuLocation}
        mode={mode}
        onFallbackAnalyticsClick={onCliFallbackAnalyticsClick}
      />
      <ActionList.Divider />
      <EditorMenuItems menuLocation={menuLocation} mode={mode} />
    </>
  )
}

export function EditorMenuItems({menuLocation, mode, onItemClick}: EditorMenuItemsProps) {
  return Object.entries(editors).map(([key, {name, url, icon}]) => (
    <ActionList.LinkItem
      key={key}
      href={key === 'xcode' && copilotFeatureFlags.xcodeInstallPage ? xcodeInstallPageUrl : url}
      {...NEW_WINDOW_LINK_PROPS}
      onClick={() => {
        onItemClick?.()
        if (copilotFeatureFlags.freeToPaidTelemetry) {
          recordMenuClick({eventName: key, menuLocation, mode, action: 'menu_item_click', text: name})
        }
      }}
    >
      {renderNewWindowLabel(name)}
      <ActionList.LeadingVisual>
        {/* Icons should not have alt text since they are decorative and redundant with the editor name */}
        <img src={icon} alt="" height="20" width="20" />
      </ActionList.LeadingVisual>
    </ActionList.LinkItem>
  ))
}
